import { motion, AnimatePresence } from "framer-motion";
import {
  GeneratePostRequestPlatform,
  GeneratePostRequestPostLengthOverride,
  GeneratePostRequestPostStyle,
  GeneratePostRequestSpacingStyle,
  GeneratePostRequestHookStyle,
  GeneratePostRequestPerspective,
  GeneratePostRequestHumorLevel,
  GeneratePostRequestAudience,
  GeneratePostRequestEmojiUsage,
} from "@workspace/api-client-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Wand2, Loader2, Layers, AlignLeft, X, Sparkles } from "lucide-react";
import { NudgeHint } from "@/components/nudge-hint";
import { AdvancedOptionsPanel } from "@/components/advanced-options-panel";
import { VoiceInputButton, InlineRecordingIndicator } from "@/components/voice-input-button";
import { PLATFORM_LENGTH_HINTS } from "@/lib/post-generator-config";
import type { UseInlineVoiceInputReturn } from "@/hooks/use-inline-voice-input";
import type { ConflictWarning } from "@/lib/post-generator-config";

type VoicePreferences = { postLength?: string | null };

interface PostGeneratorInputPanelProps {
  mode: "single" | "all";
  platform: GeneratePostRequestPlatform;
  setLocalPlatform: (p: GeneratePostRequestPlatform) => void;
  currentIdea: string;
  setIdea: (v: string) => void;
  voiceIntentBanner: string | null;
  setVoiceIntentBanner: (v: string | null) => void;
  topicVoiceInput: UseInlineVoiceInputReturn;
  handleVoiceTranscriptForTopic: (text: string) => void;
  isIdeaVague: boolean;
  tone: number[];
  setTone: (v: number[]) => void;
  postLengthOverride: GeneratePostRequestPostLengthOverride | undefined;
  setPostLengthOverride: (v: GeneratePostRequestPostLengthOverride | undefined) => void;
  voiceProfilePreferences: VoicePreferences | null | undefined;
  advancedOpen: boolean;
  setAdvancedOpen: (v: boolean) => void;
  advancedCount: number;
  platformDesc: Record<string, string>;
  currentSuggestions: object;
  conflicts: ConflictWarning[];
  voiceStates: Array<{ label: string; description: string; dominantTone: string; exampleContext: string }> | undefined;
  selectedVoiceState: string | undefined;
  setSelectedVoiceState: (v: string | undefined) => void;
  audienceDescription: string;
  setAudienceDescription: (v: string) => void;
  postStyle: GeneratePostRequestPostStyle | undefined;
  setPostStyle: (v: GeneratePostRequestPostStyle | undefined) => void;
  hookStyle: GeneratePostRequestHookStyle | undefined;
  setHookStyle: (v: GeneratePostRequestHookStyle | undefined) => void;
  spacingStyle: GeneratePostRequestSpacingStyle | undefined;
  setSpacingStyle: (v: GeneratePostRequestSpacingStyle | undefined) => void;
  perspective: GeneratePostRequestPerspective | undefined;
  setPerspective: (v: GeneratePostRequestPerspective | undefined) => void;
  humorLevel: GeneratePostRequestHumorLevel | undefined;
  setHumorLevel: (v: GeneratePostRequestHumorLevel | undefined) => void;
  audience: GeneratePostRequestAudience | undefined;
  setAudience: (v: GeneratePostRequestAudience | undefined) => void;
  emojiUsage: GeneratePostRequestEmojiUsage | undefined;
  setEmojiUsage: (v: GeneratePostRequestEmojiUsage | undefined) => void;
  targetWordCount: string;
  setTargetWordCount: (v: string) => void;
  customInstruction: string;
  setCustomInstruction: (v: string) => void;
  blendStyle: string | undefined;
  setBlendStyle: (v: string | undefined) => void;
  recentTopics: Array<{ topic: string; format: string }>;
  clearRecentContext: () => void;
  clearAdvanced: () => void;
  multiVariant: boolean;
  setMultiVariant: (v: (prev: boolean) => boolean) => void;
  isLoading: boolean;
  handleGenerate: () => void;
}

export function PostGeneratorInputPanel({
  mode, platform, setLocalPlatform,
  currentIdea, setIdea, voiceIntentBanner, setVoiceIntentBanner,
  topicVoiceInput, handleVoiceTranscriptForTopic, isIdeaVague,
  tone, setTone, postLengthOverride, setPostLengthOverride, voiceProfilePreferences,
  advancedOpen, setAdvancedOpen, advancedCount,
  platformDesc, currentSuggestions, conflicts, voiceStates,
  selectedVoiceState, setSelectedVoiceState,
  audienceDescription, setAudienceDescription,
  postStyle, setPostStyle, hookStyle, setHookStyle,
  spacingStyle, setSpacingStyle, perspective, setPerspective,
  humorLevel, setHumorLevel, audience, setAudience,
  emojiUsage, setEmojiUsage, targetWordCount, setTargetWordCount,
  customInstruction, setCustomInstruction, blendStyle, setBlendStyle,
  recentTopics, clearRecentContext, clearAdvanced,
  multiVariant, setMultiVariant, isLoading, handleGenerate,
}: PostGeneratorInputPanelProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex flex-col gap-4"
    >
      <div className="bg-card border border-border rounded-3xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <AlignLeft className="w-5 h-5 text-primary" /> Your Idea
          </h2>
          {mode === "single" && (
            <select
              value={platform}
              onChange={(e) => setLocalPlatform(e.target.value as GeneratePostRequestPlatform)}
              className="bg-secondary text-sm font-medium border-0 rounded-lg px-3 py-1.5 focus:ring-2 ring-primary/50 outline-none"
            >
              <option value="twitter">Twitter/X</option>
              <option value="linkedin">LinkedIn</option>
              <option value="reddit">Reddit</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
            </select>
          )}
          {mode === "all" && (
            <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded-lg">5 platforms</span>
          )}
        </div>

        <AnimatePresence>
          {voiceIntentBanner && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-primary/8 border border-primary/25 text-xs text-primary mb-2"
              data-testid="voice-intent-banner"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 shrink-0" />
                <span>{voiceIntentBanner}</span>
              </div>
              <button
                onClick={() => setVoiceIntentBanner(null)}
                className="shrink-0 hover:text-primary/60 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative">
          <Textarea
            value={currentIdea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="What's on your mind? Be as messy as you want..."
            className="resize-none min-h-[160px] bg-background/50 border-border rounded-2xl p-4 text-base focus-visible:ring-primary/30"
            data-testid="topic-textarea"
          />
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <VoiceInputButton
            voiceInput={topicVoiceInput}
            onTranscript={handleVoiceTranscriptForTopic}
            showLabel={true}
            data-testid="topic-voice-button"
          />
          <AnimatePresence>
            {topicVoiceInput.isRecording && (
              <InlineRecordingIndicator
                recordingTime={topicVoiceInput.recordingTime}
                formatTime={topicVoiceInput.formatTime}
              />
            )}
          </AnimatePresence>
        </div>
        <NudgeHint isVague={isIdeaVague} sessionDismissKey="post-generator" />
        <p className="mt-1.5 text-xs text-muted-foreground/55 leading-relaxed">
          Got a story behind this? Jot it down — the AI will use it exactly as you tell it.
        </p>

        <div className="mt-5 space-y-3">
          <div className="flex justify-between items-center text-sm font-medium text-muted-foreground">
            <span>Casual</span>
            <span className="text-foreground font-display">Tone</span>
            <span>Professional</span>
          </div>
          <Slider value={tone} onValueChange={setTone} max={100} step={1} className="w-full" />
        </div>

        <div className="mt-4">
          <div className="flex justify-between items-center text-sm font-medium text-muted-foreground mb-2">
            <span className="text-foreground font-display">Length</span>
            {postLengthOverride && postLengthOverride !== voiceProfilePreferences?.postLength && (
              <button
                onClick={() => setPostLengthOverride(undefined)}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                data-testid="post-length-reset"
              >
                Reset to saved
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {(["short", "medium", "long"] as const).map((l) => {
              const savedPref = voiceProfilePreferences?.postLength;
              const effectiveDefault = savedPref ?? "medium";
              const isActive = postLengthOverride === l || (!postLengthOverride && effectiveDefault === l);
              const isSaved = savedPref === l;
              const isOverrideActive = postLengthOverride === l;
              return (
                <button
                  key={l}
                  data-testid={`post-length-${l}`}
                  onClick={() => setPostLengthOverride(postLengthOverride === l ? undefined : l)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border capitalize transition-all ${
                    isActive
                      ? "bg-primary/10 border-primary/40 text-primary"
                      : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                  }`}
                >
                  {l}
                  {isSaved && !isOverrideActive && (
                    <span className="text-[10px] ml-1 opacity-60">(saved)</span>
                  )}
                  {!savedPref && !postLengthOverride && l === "medium" && (
                    <span className="text-[10px] ml-1 opacity-60">(default)</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {mode === "single" && (
          <p className="text-[11px] text-muted-foreground mt-2">{PLATFORM_LENGTH_HINTS[platform]}</p>
        )}

        <AdvancedOptionsPanel
          advancedOpen={advancedOpen}
          setAdvancedOpen={setAdvancedOpen}
          advancedCount={advancedCount}
          platform={platform}
          currentIdea={currentIdea}
          platformDesc={platformDesc}
          currentSuggestions={currentSuggestions}
          conflicts={conflicts}
          voiceStates={voiceStates}
          selectedVoiceState={selectedVoiceState}
          setSelectedVoiceState={setSelectedVoiceState}
          audienceDescription={audienceDescription}
          setAudienceDescription={setAudienceDescription}
          postStyle={postStyle}
          setPostStyle={setPostStyle}
          hookStyle={hookStyle}
          setHookStyle={setHookStyle}
          spacingStyle={spacingStyle}
          setSpacingStyle={setSpacingStyle}
          perspective={perspective}
          setPerspective={setPerspective}
          humorLevel={humorLevel}
          setHumorLevel={setHumorLevel}
          audience={audience}
          setAudience={setAudience}
          emojiUsage={emojiUsage}
          setEmojiUsage={setEmojiUsage}
          targetWordCount={targetWordCount}
          setTargetWordCount={setTargetWordCount}
          customInstruction={customInstruction}
          setCustomInstruction={setCustomInstruction}
          blendStyle={blendStyle}
          setBlendStyle={setBlendStyle}
          recentTopics={recentTopics}
          clearRecentContext={clearRecentContext}
          clearAdvanced={clearAdvanced}
        />

        <div className="mt-5 space-y-2">
          {mode === "single" && (
            <div className="flex items-center justify-end">
              <button
                data-testid="multi-variant-toggle"
                onClick={() => setMultiVariant((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  multiVariant
                    ? "bg-primary/15 border-primary/40 text-primary"
                    : "bg-secondary/50 border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                }`}
              >
                <Layers className="w-3 h-3" />
                3 versions
              </button>
            </div>
          )}
          <Button
            onClick={handleGenerate}
            disabled={isLoading || !currentIdea.trim()}
            aria-busy={isLoading}
            className="w-full h-14 rounded-xl text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Wand2 className="w-5 h-5 mr-2" />
            )}
            {isLoading
              ? "Channeling your voice..."
              : mode === "all"
              ? "Generate for All 5 Platforms"
              : multiVariant
              ? "Generate 3 Versions"
              : "Generate Post"
            }
          </Button>
        </div>
      </div>

    </motion.div>
  );
}
